/**
 * The request list screen (spec 8.4) — Phase 2B, Task 14.
 *
 * A sibling file rather than more keys in `reviewRequests.composer.ts`, for
 * the reason `reviewRequests.confirm.ts` and `reviewRequests.scholars.ts`
 * exist: that file is already 356 lines against this repo's 400-line hard
 * limit. The whole `list` object lives here and is spread back into the SAME
 * `reviewRequests.requests` namespace, so every key below is still
 * `reviewRequests.requests.list.*` and no import path anywhere changed.
 *
 * Same two constraints as its siblings: English only, and `{named}`
 * placeholders only — `cspSafeMessageCompiler` cannot parse vue-i18n's `|`
 * plural syntax, so count-dependent text is a manual `_one` / `_other` pair.
 *
 * ## Empty is not an error, and the copy has to say so
 *
 * `GET /requests` FILTERS by the caller's sectors rather than denying
 * (`handlers/request_read.rs`'s module doc: 404-ing a list would itself
 * confirm that out-of-sector clients have requests). So an empty page is the
 * ordinary "nothing in scope" answer, and `empty` below is written as a
 * statement about scope — never as a failure, and never beside a retry
 * button, which is what would make a SAM report a working screen as broken.
 */
export const reviewRequestsListMessages = {
  list: {
    breadcrumb: "Review Requests",
    title: "Review requests",
    // Task 6: names both interactions this screen now supports (a row is a
    // link, a heading is a sort control), since neither is otherwise labelled
    // on the page itself.
    intro:
      "Every review request for clients in your sectors. Select a row to open it — a draft opens where you left off; select a column heading to sort.",
    // Opens the client picker (Task 8) — the module's one entry point for
    // starting a request (spec D1).
    newRequest: "New review request",

    searchLabel: "Search requests",
    searchPlaceholder: "Search reference, client or product",
    searchClear: "Clear search",

    // The ordinary answer, not a failure — see this file's module doc.
    empty: "No review requests are in scope for you yet.",
    // Replaces the status-only wording now that search can also empty the
    // page: naming "these filters" covers a search term, a status, or both.
    emptyFiltered: "No review requests match these filters in your sectors.",
    // A genuine failure, which the two above deliberately are not.
    loadError: "Could not load the review requests. Please try again.",

    caption: "Review requests in your sectors",
    colCode: "Reference",
    colClient: "Client",
    colProduct: "Product",
    colStatus: "Status",
    // Rendered only for a caller who can see more than one sector — a column
    // whose every cell repeats the same value is noise on the one screen that
    // has to stay scannable.
    colSector: "Sector",
    colSubmitted: "Sent",
    // Visually hidden last heading, above the row's trailing chevron — the
    // column has no visible label, but a screen reader still needs one.
    openColumn: "Open",

    // `review_code` is assigned by the submit transaction, so a draft has
    // none. Said in words rather than left as a blank cell, which reads as
    // data that failed to load.
    codePending: "Not yet assigned",
    // `client_name` and `sector_name` are LEFT-joined and nullable.
    clientUnknown: "Unknown client",
    sectorUnknown: "No sector",
    notSubmitted: "Not sent yet",
    // A draft row opens the composer, not the record: said to screen readers
    // inside the row's link (the pencil beside it is decorative).
    continueDraft: "Continue editing",

    // `ALLOWED_STATUSES` (`handlers/request_read.rs`) — a code on the wire,
    // a sentence here, exactly as every other backend code in this module.
    status: {
      draft: "Draft",
      // Was "Queued" through Phase 2B, when nothing drained the outbox. Kept
      // byte-identical to `dispatch.queued` on the request page: two screens
      // wording one fact differently is what the original comment here warned
      // about.
      dispatching: "Sending",
      in_review: "In review",
      completed: "Completed",
      cancelled: "Cancelled",
    },
    // The status filter is a segmented button group now, not a `<select>`:
    // `statusGroupLabel` names the group, and each button carries its own
    // `aria-pressed`. "Any status" read oddly as a button's own label, so it
    // becomes "All" (`filterLabel`, the `<select>`'s own label, no longer
    // applies to a group of buttons and is retired with it).
    statusGroupLabel: "Filter by status",
    filterAll: "All",

    // From the envelope's `total`, never from the rows on screen: `total` is
    // computed under the same predicate as `items`, and counting the received
    // rows would produce arithmetic the SAM cannot page through.
    showing_one: "Showing {from}–{to} of 1 request",
    showing_other: "Showing {from}–{to} of {total} requests",

    // The live region Task 6 adds beside the table: announces the count on
    // every completed load (search, filter, sort or page), the same total the
    // showing line already reads from the envelope.
    found_one: "1 request found",
    found_other: "{count} requests found",

    // `validate_paging` refuses a `per_page` outside 1..=100 rather than
    // clamping it (`handlers/request_read.rs::MAX_PER_PAGE`), because a
    // client that asked for 500 and silently got 100 would compute the wrong
    // next offset. Refused here too, so the refusal is a sentence rather than
    // a 422 with no page of data behind it.
    perPageRefused: "A page can show between 1 and {max} requests.",

    // The "New review request" client picker (Task 8, `NewRequestDialog.vue`),
    // the module's one entry point for starting a request (spec D1).
    picker: {
      title: "New review request",
      intro: "Choose the client this review is for.",
      searchLabel: "Search clients",
      searchPlaceholder: "Search by client name, alias or short code",
      close: "Close",
      listLabel: "Clients in your sectors",
      scopeNote: "Showing clients in your sectors only",
      compose: "Compose",
      noMatch: "No client matches “{query}”",
      // Controller ruling R2: the secondary line under `noMatch`, restating
      // why the list is scoped rather than global.
      noMatchHint: "Only clients in your assigned sectors are listed.",
      noClients: "There are no active clients in your sectors.",
      loadError: "Could not load your clients.",
      retry: "Try again",
      hintMove: "move",
      hintOpen: "compose",
      hintClose: "close",
    },
  },
};
